import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, shareReplay, catchError, map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { ImageFallbackService } from './image-fallback.service';

interface CachedData<T> {
  data: T;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class emojisService {
  private apiUrl = environment.apiUrl;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

  // ── Mapa en memoria: acceso O(1) síncrono desde cualquier pipe/componente ──
  readonly emojiMap = new Map<string, string>(); // shortcode → url
  readonly emojiIdMap = new Map<string, number>(); // shortcode → id_emoji

  // Cache de los emojis del usuario
  private userEmojisCache: CachedData<any[]> | null = null;
  // Cache de los favoritos del usuario (misma duración que emojis)
  private userFavoritesCache: CachedData<any[]> | null = null;

  // Observable de estado de carga: emite true cuando el mapa ya está listo
  private readonly ready$ = new BehaviorSubject<boolean>(false);
  readonly whenReady$ = this.ready$.asObservable();

  // Observable de fetch compartido — se suscribe UNA sola vez en toda la app
  private fetch$: Observable<any> | null = null;

  private imageFallback = inject(ImageFallbackService);

  constructor(private http: HttpClient) {
    this.initMap();
  }

  // ── HELPERS DE CACHE ──────────────────────────────────────────────────────

  private isCacheValid<T>(cache: CachedData<T> | null): boolean {
    if (!cache) return false;
    return Date.now() - cache.timestamp < this.CACHE_DURATION;
  }

  // ── MAPA GLOBAL DE EMOJIS ─────────────────────────────────────────────────

  /** Carga el mapa de emojis una única vez al arrancar el servicio */
  private initMap(): void {
    if (!this.fetch$) {
      this.fetch$ = this.http.get<any>(`${this.apiUrl}/emoji-map`).pipe(
        tap((response) => this.populateMap(response)),
        shareReplay(1),
      );
    }
    this.fetch$.subscribe({
      next: () => this.ready$.next(true),
      error: (err) => console.error('Error cargando mapa de emojis:', err),
    });
  }

  private populateMap(response: any): void {
    this.emojiMap.clear();
    this.emojiIdMap.clear();
    const source = response?.emoji_map ?? response ?? {};
    for (const [shortcode, value] of Object.entries(source)) {
      const key = shortcode.toLowerCase();
      // Soporte para formato nuevo { url, id } y formato legacy string
      if (value && typeof value === 'object' && 'url' in (value as any)) {
        this.emojiMap.set(
          key,
          this.imageFallback.getConstructedUrl(
            (value as any).url as string,
            'emoji',
          ),
        );
        this.emojiIdMap.set(key, (value as any).id as number);
      } else {
        this.emojiMap.set(
          key,
          this.imageFallback.getConstructedUrl(value as string, 'emoji'),
        );
      }
    }
  }

  /**
   * Devuelve { url, id_emoji } para un shortcode dado, o null si no existe.
   * Útil para abrir el EmojiDetailComponent desde contenido renderizado via innerHTML.
   */
  getEmojiByShortcode(
    shortcode: string,
  ): { file_url: string; id_emoji: number } | null {
    const key = shortcode.toLowerCase();
    const url = this.emojiMap.get(key);
    const id = this.emojiIdMap.get(key);
    if (!url || id === undefined) return null;
    return { file_url: url, id_emoji: id }; // ← renombrado
  }

  /** Fuerza recarga del mapa desde la API (por si hay inconsistencias) */
  refreshMap(): void {
    this.fetch$ = null;
    this.ready$.next(false);
    this.initMap();
  }

  // ── CRUD ───────────────────────────────────────────────────────────────────

  /**
   * Devuelve los emojis del usuario.
   * Sirve desde caché si está vigente; si no, llama a la API y guarda en caché.
   */
  getUseremojis(): Observable<any> {
    if (this.isCacheValid(this.userEmojisCache)) {
      return of({
        status: true,
        emojis: this.userEmojisCache!.data,
        fromCache: true,
      });
    }

    return this.http.get<any>(`${this.apiUrl}/user/emojis`).pipe(
      map((response: any) => {
        if (response.status && response.emojis) {
          response.emojis = response.emojis.map((emoji: any) => ({
            ...emoji,
            file_url: this.imageFallback.getConstructedUrl(
              emoji.file_url,
              'emoji',
            ),
          }));
        }
        return response;
      }),
      tap((response: any) => {
        if (response.status) {
          this.userEmojisCache = {
            data: response.emojis || [],
            timestamp: Date.now(),
          };
        }
      }),
      catchError((error) => {
        // Si hay caché aunque sea caducada, úsala ante un error de red
        if (this.userEmojisCache) {
          console.warn(
            '⚠️ Error de red: mostrando emojis desde caché caducada',
          );
          return of({
            status: true,
            emojis: this.userEmojisCache.data,
            fromCache: true,
            apiError: true,
          });
        }
        throw error;
      }),
    );
  }

  /**
   * Devuelve los favoritos del usuario.
   * Misma estrategia de caché que getUseremojis().
   */
  getUserFavorites(): Observable<any> {
    if (this.isCacheValid(this.userFavoritesCache)) {
      return of({
        status: true,
        favorites: this.userFavoritesCache!.data,
        fromCache: true,
      });
    }

    return this.http.get<any>(`${this.apiUrl}/user/favorites`).pipe(
      map((response: any) => {
        if (response.status && response.favorites) {
          response.favorites = response.favorites.map((emoji: any) => ({
            ...emoji,
            file_url: this.imageFallback.getConstructedUrl(
              emoji.file_url,
              'emoji',
            ),
          }));
        }
        return response;
      }),
      tap((response: any) => {
        if (response.status) {
          this.userFavoritesCache = {
            data: response.favorites || [],
            timestamp: Date.now(),
          };
        }
      }),
      catchError((error) => {
        if (this.userFavoritesCache) {
          console.warn(
            '⚠️ Error de red: mostrando favoritos desde caché caducada',
          );
          return of({
            status: true,
            favorites: this.userFavoritesCache.data,
            fromCache: true,
            apiError: true,
          });
        }
        throw error;
      }),
    );
  }

  addToFavorites(emojiId: number): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/favorites`, { emoji_id: emojiId })
      .pipe(
        tap((response) => {
          // Invalidar caché de favoritos para forzar re-fetch en la próxima llamada
          if (response?.status) {
            this.userFavoritesCache = null;
          }
        }),
      );
  }

  removeFromFavorites(emojiId: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/favorites/${emojiId}`).pipe(
      tap(() => {
        // Quitar quirúrgicamente del caché sin re-fetchear
        if (this.userFavoritesCache) {
          this.userFavoritesCache.data = this.userFavoritesCache.data.filter(
            (f) => (f.id_emoji ?? f.pivot?.emoji_id) !== emojiId,
          );
        }
      }),
    );
  }

  reorderFavorites(order: number[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/favorites/reorder`, { order });
  }

  /**
   * Crea un emoji y lo agrega quirúrgicamente al cache + al emojiMap,
   * sin necesitar re-fetchear la lista completa.
   */
  createemoji(emojiData: FormData): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/emojis`, emojiData).pipe(
      map((response: any) => {
        if (response?.status && response?.emoji) {
          response.emoji.file_url = this.imageFallback.getConstructedUrl(
            response.emoji.file_url,
            'emoji',
          );
        }
        return response;
      }),
      tap((response: any) => {
        if (response?.status && response?.emoji) {
          const emoji = response.emoji;

          // 1. Agregar al cache de lista de usuario
          if (this.userEmojisCache) {
            this.userEmojisCache.data = [...this.userEmojisCache.data, emoji];
            console.log(`✅ Emoji "${emoji.shortcode}" agregado al caché`);
          }

          // 2. Actualizar el mapa global en memoria sin re-fetchear
          if (emoji.shortcode && emoji.file_url) {
            this.emojiMap.set(emoji.shortcode.toLowerCase(), emoji.file_url);
            this.emojiIdMap.set(emoji.shortcode.toLowerCase(), emoji.id_emoji);
            this.ready$.next(true);
            console.log(`🗺️ emojiMap actualizado con ":${emoji.shortcode}:"`);
          }
        } else {
          // La API no devolvió el emoji completo: refrescar mapa como fallback
          console.warn(
            '⚠️ createemoji: la API no devolvió el emoji completo, refrescando mapa',
          );
          this.refreshMap();
          this.userEmojisCache = null; // Forzar re-fetch en la próxima llamada
        }
      }),
    );
  }

  /**
   * Elimina un emoji y lo quita quirúrgicamente del cache + del emojiMap.
   */
  deleteemoji(id_emoji: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/emojis/${id_emoji}`).pipe(
      tap(() => {
        if (this.userEmojisCache) {
          const removed = this.userEmojisCache.data.find(
            (e) => e.id_emoji === id_emoji,
          );
          // Quitar del array en memoria
          this.userEmojisCache.data = this.userEmojisCache.data.filter(
            (e) => e.id_emoji !== id_emoji,
          );
          // Quitar del mapa global
          if (removed?.shortcode) {
            this.emojiMap.delete(removed.shortcode.toLowerCase());
            this.emojiIdMap.delete(removed.shortcode.toLowerCase());
            console.log(`🗺️ emojiMap: eliminado ":${removed.shortcode}:"`);
          }
          console.log(`🗑️ Emoji ${id_emoji} eliminado del caché`);
        } else {
          // Si no hay caché de lista, al menos actualizamos el mapa
          this.refreshMap();
        }
      }),
    );
  }
}
